// CMS.js - Content Management System para Landing Page
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { uploadImage, validateFile, getHomeImagesFromAPI } from '../services/cmsService';
import { getS3ImageUrl } from '../services/cmsService';

// Valores por defecto del contenido
const defaultTexts = {
  hero: {
    title: 'UNIDOS EN LA PLATAFORMA',
    subtitle: 'FORJADOS EN ACERO Y ESFUERZO. Cada levantamiento, una prueba de voluntad.'
  },
  carousel: [
    { id: 1, title: '¿QUIERES INICIAR EN EL POWERLIFTING?', subtitle: 'No es tan difícil como crees...' },
    { id: 2, title: 'DESDE DONDE LEVANTAMOS', subtitle: 'La plataforma es su escenario' },
    { id: 3, title: 'MÁS ALLÁ DE LA PLATAFORMA', subtitle: 'Un recordatorio para apostar por ti' },
    { id: 4, title: 'NACIDO PARA SUBIR EP 1', subtitle: '¿Subirás la barra?' },
    { id: 5, title: 'COMPRA EL ÍCONO', subtitle: 'El nuevo lanzamiento está aquí' }
  ],
  events: {
    preTitle: 'COMPITE',
    mainTitle: 'EVENTOS DESTACADOS',
    items: [
      { id: 1, location: 'SHREVEPORT, LA', date: '7 de Marzo, 2026' },
      { id: 2, location: 'PHOENIX, ARIZONA', date: '28 de Marzo, 2026' },
      { id: 3, location: 'ANAHEIM, CA', date: '25-30 de Mayo, 2026' },
      { id: 4, location: 'AUSTIN, TX', date: '15 de Junio, 2026' }
    ]
  },
  banner: {
    title: 'ACHIPO COLECCIÓN ÍCONO',
    buttonText: 'Comprar Ahora'
  },
  contentCards: [
    { id: 1, title: 'MEMBRESÍA', description: 'Únete a la familia ACHIPO o renueva tu membresía rápida y fácilmente.', linkText: 'UNIRSE / RENOVAR' },
    { id: 2, title: 'REGLAMENTOS', description: 'Consulta el reglamento oficial, estatutos y especificaciones técnicas para todas las competiciones.', linkText: 'VER REGLAMENTOS' },
    { id: 3, title: 'ÚLTIMAS NOTICIAS', description: 'Mantente al día con los artículos, anuncios y levantadores destacados más recientes.', linkText: 'LEER NOTICIAS' }
  ]
};

const defaultImages = {
  hero_bg: null,
  carousel: [
    { id: 1, image: null },
    { id: 2, image: null },
    { id: 3, image: null },
    { id: 4, image: null },
    { id: 5, image: null }
  ],
  events: [
    { id: 1, image: null },
    { id: 2, image: null },
    { id: 3, image: null },
    { id: 4, image: null }
  ],
  banner_bg: null,
  page_hero_rules: null,
  page_hero_events: null,
  page_hero_members: null,
  page_hero_shop: null,
  page_hero_associates: null,
  page_hero_records: null,
  page_hero_rankings: null,
  page_hero_clasificacion: null,
  page_hero_login: null,
  page_hero_profile: null
};

const CMS = () => {
  const [activeTab, setActiveTab] = useState('images');
  const [selectedSection, setSelectedSection] = useState('hero_bg');
  const [imagePreview, setImagePreview] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [liveImages, setLiveImages] = useState({});
  const [isLoadingLive, setIsLoadingLive] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // Colores (mismo estilo que AdminPanel)
  const colors = {
    primary: '#1a1a1a',
    secondary: '#666',
    accent: '#FF4500',
    accentLight: '#e63e00',
    lightGray: '#f8f9fa',
    mediumGray: '#eaeaea',
    darkGray: '#333',
    white: '#ffffff',
    green: '#00aa55',
    blue: '#0066cc',
    purple: '#9c27b0',
    red: '#dc3545'
  };

  // Estado para textos
  const [textsData, setTextsData] = useState(() => {
    const saved = localStorage.getItem('cms_texts');
    return saved ? JSON.parse(saved) : defaultTexts;
  });

  // Estado para imágenes
  const [imagesData, setImagesData] = useState(() => {
    const saved = localStorage.getItem('cms_images');
    return saved ? JSON.parse(saved) : defaultImages;
  });

  // Función para guardar cambios manualmente
  const handleSaveChanges = () => {
    localStorage.setItem('cms_texts', JSON.stringify(textsData));
    localStorage.setItem('cms_images', JSON.stringify(imagesData));
    setHasUnsavedChanges(false);
    setSaveMessage('Cambios guardados correctamente');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  // Función para guardar cambios de imágenes y resetear formulario
  const handleSaveImageChanges = () => {
    localStorage.setItem('cms_images', JSON.stringify(imagesData));
    setHasUnsavedChanges(false);
    setSaveMessage('Cambios de imágenes guardados correctamente');
    setTimeout(() => setSaveMessage(''), 3000);

    // Resetear formulario
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Verificar autenticación
  useEffect(() => {
    const isAdmin = localStorage.getItem('isAdmin') === 'true';
    if (!isAdmin) {
      navigate('/login');
    }
  }, [navigate]);

  // Limpiar preview de nueva imagen al cambiar sección
  useEffect(() => {
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [selectedSection]);

  // Cargar imágenes home desde API al abrir el tab (una sola llamada al backend)
  useEffect(() => {
    if (activeTab !== 'images') return;
    setIsLoadingLive(true);
    getHomeImagesFromAPI()
      .then(imgs => {
        const flat = { hero_bg: imgs.hero_bg, banner_bg: imgs.banner_bg };
        imgs.carousel.forEach(item => { flat[`carousel_${item.id}`] = item.image; });
        imgs.events.forEach(item => { flat[`event_${item.id}`] = item.image; });
        setLiveImages(prev => ({ ...prev, ...flat }));
      })
      .catch(() => {})
      .finally(() => setIsLoadingLive(false));
  }, [activeTab]);

  // Cargar imagen actual de S3 bajo demanda si la sección no está en liveImages aún
  useEffect(() => {
    if (liveImages.hasOwnProperty(selectedSection)) return;
    setIsLoadingLive(true);
    getS3ImageUrl(selectedSection)
      .then(url => setLiveImages(prev => {
        if (prev.hasOwnProperty(selectedSection)) return prev;
        return { ...prev, [selectedSection]: url };
      }))
      .catch(() => setLiveImages(prev => ({ ...prev, [selectedSection]: null })))
      .finally(() => setIsLoadingLive(false));
  }, [selectedSection]);

  // Manejar subida de imagen
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar archivo antes de mostrar preview
    const validation = validateFile(file);
    if (!validation.valid) {
      setUploadError(validation.error);
      return;
    }

    // Limpiar errores previos
    setUploadError(null);

    // Mostrar preview local inmediato (mejor UX)
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Subir via backend CMS
    setIsUploading(true);
    try {
      const result = await uploadImage(file, selectedSection);

      // Guardar URL en el estado correspondiente
      const imageUrl = result.url;

      if (selectedSection === 'hero_bg') {
        setImagesData(prev => ({ ...prev, hero_bg: imageUrl }));
      } else if (selectedSection === 'banner_bg') {
        setImagesData(prev => ({ ...prev, banner_bg: imageUrl }));
      } else if (selectedSection.startsWith('carousel_')) {
        const index = parseInt(selectedSection.split('_')[1]) - 1;
        setImagesData(prev => ({
          ...prev,
          carousel: prev.carousel.map((item, i) =>
            i === index ? { ...item, image: imageUrl } : item
          )
        }));
      } else if (selectedSection.startsWith('event_')) {
        const index = parseInt(selectedSection.split('_')[1]) - 1;
        setImagesData(prev => ({
          ...prev,
          events: prev.events.map((item, i) =>
            i === index ? { ...item, image: imageUrl } : item
          )
        }));
      } else if (selectedSection.startsWith('page_hero_')) {
        setImagesData(prev => ({ ...prev, [selectedSection]: imageUrl }));
      }

      // Actualizar imagen en producción para reflejar la recién subida
      setLiveImages(prev => ({ ...prev, [selectedSection]: imageUrl }));
      // Limpiar preview de nueva imagen (ya está en producción)
      setImagePreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';

      setHasUnsavedChanges(true);
      setSaveMessage('Imagen subida correctamente');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      setUploadError(error.message);
      setImagePreview(null); // Limpiar preview si falló
    } finally {
      setIsUploading(false);
    }
  };

  // Eliminar imagen
  const handleRemoveImage = () => {
    setImagePreview(null);
    setHasUnsavedChanges(true);

    if (selectedSection === 'hero_bg') {
      setImagesData(prev => ({ ...prev, hero_bg: null }));
    } else if (selectedSection === 'banner_bg') {
      setImagesData(prev => ({ ...prev, banner_bg: null }));
    } else if (selectedSection.startsWith('carousel_')) {
      const index = parseInt(selectedSection.split('_')[1]) - 1;
      setImagesData(prev => ({
        ...prev,
        carousel: prev.carousel.map((item, i) =>
          i === index ? { ...item, image: null } : item
        )
      }));
    } else if (selectedSection.startsWith('event_')) {
      const index = parseInt(selectedSection.split('_')[1]) - 1;
      setImagesData(prev => ({
        ...prev,
        events: prev.events.map((item, i) =>
          i === index ? { ...item, image: null } : item
        )
      }));
    }
  };

  // Manejar cambio de textos
  const handleTextChange = (section, field, value, itemIndex = null) => {
    setHasUnsavedChanges(true);
    setTextsData(prev => {
      if (itemIndex !== null) {
        // Para arrays (carousel, events.items, contentCards)
        if (section === 'carousel') {
          return {
            ...prev,
            carousel: prev.carousel.map((item, i) =>
              i === itemIndex ? { ...item, [field]: value } : item
            )
          };
        } else if (section === 'events.items') {
          return {
            ...prev,
            events: {
              ...prev.events,
              items: prev.events.items.map((item, i) =>
                i === itemIndex ? { ...item, [field]: value } : item
              )
            }
          };
        } else if (section === 'contentCards') {
          return {
            ...prev,
            contentCards: prev.contentCards.map((item, i) =>
              i === itemIndex ? { ...item, [field]: value } : item
            )
          };
        }
      } else {
        // Para objetos simples (hero, banner, events root)
        if (section === 'events') {
          return {
            ...prev,
            events: { ...prev.events, [field]: value }
          };
        }
        return {
          ...prev,
          [section]: { ...prev[section], [field]: value }
        };
      }
      return prev;
    });
  };

  // Resetear a valores por defecto
  const handleReset = () => {
    if (window.confirm('¿Estás seguro de que quieres restaurar todos los valores por defecto? Se perderán todos los cambios.')) {
      setTextsData(defaultTexts);
      setImagesData(defaultImages);
      setImagePreview(null);
    }
  };

  // URL de la imagen actualmente en producción para la sección seleccionada
  const currentLiveImage = liveImages[selectedSection] ?? null;

  // Estilos de input reutilizables
  const inputStyle = {
    width: '100%',
    padding: '10px',
    border: `1px solid ${colors.mediumGray}`,
    borderRadius: '4px',
    fontSize: '0.95em',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '0.9em',
    fontWeight: '500',
    color: colors.primary
  };

  // Opciones del selector de sección de imágenes (sin eventos; se gestionan en Admin > Eventos)
  const imageSections = [
    { value: 'hero_bg', label: 'Inicio · Hero (fondo principal)' },
    { value: 'carousel_1', label: 'Inicio · Paso 1: Aprende lo básico' },
    { value: 'carousel_2', label: 'Inicio · Paso 2: Encuentra un evento' },
    { value: 'carousel_3', label: 'Inicio · Paso 3: Únete a ACHIPO' },
    { value: 'carousel_4', label: 'Inicio · Paso 4: Mide tu nivel' },
    { value: 'carousel_5', label: 'Carrusel 5 (sin uso en el inicio)' },
    { value: 'event_1', label: 'Inicio · Afiche de respaldo, evento destacado 1' },
    { value: 'event_2', label: 'Inicio · Afiche de respaldo, evento destacado 2' },
    { value: 'event_3', label: 'Inicio · Afiche de respaldo, evento destacado 3' },
    { value: 'event_4', label: 'Inicio · Afiche de respaldo, evento destacado 4' },
    { value: 'banner_bg', label: 'Inicio · Fondo colección ícono (tienda)' },
    { value: 'page_hero_rules', label: 'Página Reglamentos - Hero' },
    { value: 'page_hero_events', label: 'Página Eventos - Hero' },
    { value: 'page_hero_members', label: 'Página Miembros - Hero' },
    { value: 'page_hero_shop', label: 'Página Tienda - Hero' },
    { value: 'page_hero_associates', label: 'Página Asociados - Hero' },
    { value: 'page_hero_records', label: 'Página Récords - Hero' },
    { value: 'page_hero_rankings', label: 'Página Rankings - Hero' },
    { value: 'page_hero_clasificacion', label: 'Página Clasificación y DOTS - Hero' },
    { value: 'page_hero_login', label: 'Página Login - Imagen lateral' },
    { value: 'page_hero_profile', label: 'Página Mi Perfil - Cabecera' }
  ];

  return (
    <div style={{
      fontFamily: 'Arial, sans-serif',
      width: '100%',
      margin: '0',
      backgroundColor: colors.white,
      minHeight: '100vh',
      color: colors.primary
    }}>
      {/* Estilos CSS para animaciones y espaciado del título CMS */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .cms-title-wrapper {
            padding-top: 6rem !important;
            padding-bottom: 0.5rem;
          }
          .cms-title-wrapper .cms-main-title {
            margin: 0 0 0.5rem 0;
            font-size: 2rem;
            font-weight: 700;
            line-height: 1.2;
            letter-spacing: -0.02em;
          }
          .cms-title-wrapper .cms-subtitle {
            margin: 0;
            font-size: 1rem;
            color: #666;
            line-height: 1.4;
          }
        `}
      </style>
      <Header />

      {/* Toast de éxito */}
      {saveMessage && (
        <div style={{
          position: 'fixed',
          top: '100px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
          padding: '16px 32px',
          borderRadius: '8px',
          fontSize: '1em',
          fontWeight: '500',
          borderLeft: '4px solid #00aa55',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          animation: 'fadeIn 0.3s ease'
        }}>
          <span style={{ fontSize: '1.2em' }}>&#10003;</span>
          {saveMessage}
        </div>
      )}

      <div style={{
        padding: '40px 20px 40px 20px',
        minHeight: '70vh',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Header: título con espacio superior moderado */}
        <div style={{
          paddingTop: '3rem',
          marginBottom: '30px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div>
            <h1 style={{
              color: colors.primary,
              margin: 0,
              marginBottom: '0.5rem',
              fontSize: '2rem',
              fontWeight: 700,
              lineHeight: 1.2
            }}>
              Gestor de Contenido (CMS)
            </h1>
            <p style={{ margin: 0, fontSize: '1rem', color: '#666', lineHeight: 1.4 }}>
              Administra las imágenes y textos del landing page
            </p>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => navigate('/admin')}
              style={{
                padding: '10px 20px',
                backgroundColor: colors.lightGray,
                color: colors.primary,
                border: `1px solid ${colors.mediumGray}`,
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.9em'
              }}
            >
              Panel Admin
            </button>

            <button
              onClick={() => navigate('/')}
              style={{
                padding: '10px 20px',
                backgroundColor: colors.accent,
                color: colors.white,
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.9em'
              }}
            >
              Ver Landing
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '30px',
          borderBottom: `1px solid ${colors.mediumGray}`,
          paddingBottom: '15px'
        }}>
          <button
            onClick={() => setActiveTab('images')}
            style={{
              padding: '10px 20px',
              backgroundColor: activeTab === 'images' ? colors.accent : colors.lightGray,
              color: activeTab === 'images' ? colors.white : colors.secondary,
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.95em'
            }}
          >
            Imágenes
          </button>
          <button
            onClick={() => setActiveTab('texts')}
            style={{
              padding: '10px 20px',
              backgroundColor: activeTab === 'texts' ? colors.accent : colors.lightGray,
              color: activeTab === 'texts' ? colors.white : colors.secondary,
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.95em'
            }}
          >
            Textos
          </button>

          <div style={{ marginLeft: 'auto' }}>
            <button
              onClick={handleReset}
              style={{
                padding: '10px 20px',
                backgroundColor: colors.red,
                color: colors.white,
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.9em'
              }}
            >
              Restaurar Todo
            </button>
          </div>
        </div>

        {/* Tab de Imágenes */}
        {activeTab === 'images' && (
          <div style={{
            backgroundColor: colors.lightGray,
            borderRadius: '6px',
            padding: '25px',
            border: `1px solid ${colors.mediumGray}`
          }}>
            <h3 style={{
              color: colors.primary,
              fontSize: '1.3em',
              marginBottom: '20px',
              fontWeight: '600'
            }}>
              Gestión de Imágenes
            </h3>

            {/* Selector de sección */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Seleccionar Sección</label>
              <select
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                style={{ ...inputStyle, maxWidth: '400px' }}
              >
                {imageSections.map(section => (
                  <option key={section.value} value={section.value}>
                    {section.label}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: '0.85em', color: colors.secondary, marginTop: '8px' }}>
                El archivo se guardará como: <strong>{selectedSection}.&#123;extensión&#125;</strong>
              </p>
            </div>

            {/* Área de upload */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '30px',
              alignItems: 'start'
            }}>
              <div>
                <label style={labelStyle}>Subir Nueva Imagen</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={isUploading}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: `2px dashed ${colors.mediumGray}`,
                    borderRadius: '4px',
                    backgroundColor: isUploading ? colors.lightGray : colors.white,
                    cursor: isUploading ? 'not-allowed' : 'pointer',
                    opacity: isUploading ? 0.6 : 1
                  }}
                />
                <p style={{
                  fontSize: '0.85em',
                  color: colors.secondary,
                  marginTop: '8px'
                }}>
                  Formatos: JPG, PNG, GIF, WebP (max 10MB)
                </p>

                {/* Indicador de carga */}
                {isUploading && (
                  <div style={{
                    marginTop: '15px',
                    padding: '12px',
                    backgroundColor: '#e3f2fd',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '3px solid #2196f3',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    <span style={{ color: '#1976d2', fontSize: '0.9em' }}>
                      Subiendo imagen...
                    </span>
                  </div>
                )}

                {/* Mensaje de error */}
                {uploadError && (
                  <div style={{
                    marginTop: '15px',
                    padding: '12px',
                    backgroundColor: '#ffebee',
                    borderRadius: '4px',
                    borderLeft: `4px solid ${colors.red}`,
                    color: colors.red,
                    fontSize: '0.9em'
                  }}>
                    {uploadError}
                  </div>
                )}

                {imagePreview && !isUploading && (
                  <button
                    onClick={handleRemoveImage}
                    style={{
                      marginTop: '15px',
                      padding: '8px 16px',
                      backgroundColor: colors.red,
                      color: colors.white,
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9em'
                    }}
                  >
                    Eliminar Imagen
                  </button>
                )}
              </div>

              {/* Paneles de preview */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                {/* Panel 1: Imagen en producción */}
                <div>
                  <label style={labelStyle}>Imagen en producción</label>
                  <div style={{
                    width: '100%',
                    minHeight: '180px',
                    backgroundColor: colors.white,
                    border: `1px solid ${colors.mediumGray}`,
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}>
                    {isLoadingLive ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: colors.secondary }}>
                        <div style={{
                          width: '18px',
                          height: '18px',
                          border: `3px solid ${colors.mediumGray}`,
                          borderTopColor: colors.secondary,
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          flexShrink: 0
                        }} />
                        <span style={{ fontSize: '0.85em' }}>Cargando...</span>
                      </div>
                    ) : currentLiveImage ? (
                      <img
                        src={currentLiveImage}
                        alt="Imagen en producción"
                        style={{ maxWidth: '100%', maxHeight: '180px', objectFit: 'contain' }}
                      />
                    ) : (
                      <span style={{ color: colors.secondary, fontSize: '0.85em' }}>
                        Sin imagen en producción
                      </span>
                    )}
                  </div>
                </div>

                {/* Panel 2: Nueva imagen (solo visible tras seleccionar archivo) */}
                {imagePreview && (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <label style={{ ...labelStyle, marginBottom: 0 }}>Nueva imagen</label>
                      <span style={{
                        fontSize: '0.75em',
                        backgroundColor: '#fff3cd',
                        color: '#856404',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontWeight: '500'
                      }}>
                        pendiente de guardar
                      </span>
                    </div>
                    <div style={{
                      width: '100%',
                      minHeight: '180px',
                      backgroundColor: colors.white,
                      border: `2px dashed ${colors.green}`,
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}>
                      <img
                        src={imagePreview}
                        alt="Nueva imagen"
                        style={{ maxWidth: '100%', maxHeight: '180px', objectFit: 'contain' }}
                      />
                    </div>
                  </div>
                )}

              </div>
            </div>

            {/* Botón Guardar Cambios */}
            <div style={{
              marginTop: '25px',
              paddingTop: '20px',
              borderTop: `1px solid ${colors.mediumGray}`,
              display: 'flex',
              alignItems: 'center',
              gap: '15px'
            }}>
              <button
                onClick={handleSaveImageChanges}
                disabled={!hasUnsavedChanges}
                style={{
                  padding: '12px 30px',
                  backgroundColor: colors.green,
                  color: colors.white,
                  border: 'none',
                  borderRadius: '6px',
                  cursor: hasUnsavedChanges ? 'pointer' : 'not-allowed',
                  fontWeight: '600',
                  fontSize: '1em',
                  transition: 'background-color 0.2s ease',
                  opacity: hasUnsavedChanges ? 1 : 0.5
                }}
                onMouseOver={(e) => { if (hasUnsavedChanges) e.target.style.backgroundColor = '#009944'; }}
                onMouseOut={(e) => { if (hasUnsavedChanges) e.target.style.backgroundColor = colors.green; }}
              >
                Guardar Cambios
              </button>
              {hasUnsavedChanges && (
                <span style={{ color: colors.accent, fontSize: '0.9em' }}>
                  * Hay cambios sin guardar
                </span>
              )}
            </div>
          </div>
        )}

        {/* Tab de Textos */}
        {activeTab === 'texts' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>

            {/* Sección Hero */}
            <div style={{
              backgroundColor: colors.lightGray,
              borderRadius: '6px',
              padding: '25px',
              border: `1px solid ${colors.mediumGray}`
            }}>
              <h3 style={{
                color: colors.primary,
                fontSize: '1.2em',
                marginBottom: '20px',
                fontWeight: '600',
                borderBottom: `2px solid ${colors.accent}`,
                paddingBottom: '10px'
              }}>
                Hero Principal
              </h3>

              <div style={{ display: 'grid', gap: '15px' }}>
                <div>
                  <label style={labelStyle}>Título</label>
                  <input
                    type="text"
                    value={textsData.hero.title}
                    onChange={(e) => handleTextChange('hero', 'title', e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Subtítulo</label>
                  <textarea
                    value={textsData.hero.subtitle}
                    onChange={(e) => handleTextChange('hero', 'subtitle', e.target.value)}
                    style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                  />
                </div>
              </div>
            </div>

            {/* Sección Carrusel */}
            <div style={{
              backgroundColor: colors.lightGray,
              borderRadius: '6px',
              padding: '25px',
              border: `1px solid ${colors.mediumGray}`
            }}>
              <h3 style={{
                color: colors.primary,
                fontSize: '1.2em',
                marginBottom: '20px',
                fontWeight: '600',
                borderBottom: `2px solid ${colors.accent}`,
                paddingBottom: '10px'
              }}>
                Carrusel de Imágenes (5 items)
              </h3>

              <div style={{ display: 'grid', gap: '20px' }}>
                {textsData.carousel.map((item, index) => (
                  <div
                    key={item.id}
                    style={{
                      backgroundColor: colors.white,
                      padding: '15px',
                      borderRadius: '4px',
                      border: `1px solid ${colors.mediumGray}`
                    }}
                  >
                    <h4 style={{
                      color: colors.secondary,
                      fontSize: '0.95em',
                      marginBottom: '12px'
                    }}>
                      Item {index + 1}
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div>
                        <label style={labelStyle}>Título</label>
                        <input
                          type="text"
                          value={item.title}
                          onChange={(e) => handleTextChange('carousel', 'title', e.target.value, index)}
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Subtítulo</label>
                        <input
                          type="text"
                          value={item.subtitle}
                          onChange={(e) => handleTextChange('carousel', 'subtitle', e.target.value, index)}
                          style={inputStyle}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sección Eventos */}
            <div style={{
              backgroundColor: colors.lightGray,
              borderRadius: '6px',
              padding: '25px',
              border: `1px solid ${colors.mediumGray}`
            }}>
              <h3 style={{
                color: colors.primary,
                fontSize: '1.2em',
                marginBottom: '20px',
                fontWeight: '600',
                borderBottom: `2px solid ${colors.accent}`,
                paddingBottom: '10px'
              }}>
                Eventos Destacados
              </h3>

              {/* Títulos de sección */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '15px',
                marginBottom: '20px'
              }}>
                <div>
                  <label style={labelStyle}>Pre-título</label>
                  <input
                    type="text"
                    value={textsData.events.preTitle}
                    onChange={(e) => handleTextChange('events', 'preTitle', e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Título Principal</label>
                  <input
                    type="text"
                    value={textsData.events.mainTitle}
                    onChange={(e) => handleTextChange('events', 'mainTitle', e.target.value)}
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Items de eventos */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                {textsData.events.items.map((item, index) => (
                  <div
                    key={item.id}
                    style={{
                      backgroundColor: colors.white,
                      padding: '15px',
                      borderRadius: '4px',
                      border: `1px solid ${colors.mediumGray}`
                    }}
                  >
                    <h4 style={{
                      color: colors.secondary,
                      fontSize: '0.95em',
                      marginBottom: '12px'
                    }}>
                      Evento {index + 1}
                    </h4>
                    <div style={{ display: 'grid', gap: '10px' }}>
                      <div>
                        <label style={labelStyle}>Ubicación</label>
                        <input
                          type="text"
                          value={item.location}
                          onChange={(e) => handleTextChange('events.items', 'location', e.target.value, index)}
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Fecha</label>
                        <input
                          type="text"
                          value={item.date}
                          onChange={(e) => handleTextChange('events.items', 'date', e.target.value, index)}
                          style={inputStyle}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sección Banner */}
            <div style={{
              backgroundColor: colors.lightGray,
              borderRadius: '6px',
              padding: '25px',
              border: `1px solid ${colors.mediumGray}`
            }}>
              <h3 style={{
                color: colors.primary,
                fontSize: '1.2em',
                marginBottom: '20px',
                fontWeight: '600',
                borderBottom: `2px solid ${colors.accent}`,
                paddingBottom: '10px'
              }}>
                Banner Icon Collection
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={labelStyle}>Título</label>
                  <input
                    type="text"
                    value={textsData.banner.title}
                    onChange={(e) => handleTextChange('banner', 'title', e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Texto del Botón</label>
                  <input
                    type="text"
                    value={textsData.banner.buttonText}
                    onChange={(e) => handleTextChange('banner', 'buttonText', e.target.value)}
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>

            {/* Sección Content Cards */}
            <div style={{
              backgroundColor: colors.lightGray,
              borderRadius: '6px',
              padding: '25px',
              border: `1px solid ${colors.mediumGray}`
            }}>
              <h3 style={{
                color: colors.primary,
                fontSize: '1.2em',
                marginBottom: '20px',
                fontWeight: '600',
                borderBottom: `2px solid ${colors.accent}`,
                paddingBottom: '10px'
              }}>
                Tarjetas de Contenido (3 cards)
              </h3>

              <div style={{ display: 'grid', gap: '20px' }}>
                {textsData.contentCards.map((card, index) => (
                  <div
                    key={card.id}
                    style={{
                      backgroundColor: colors.white,
                      padding: '15px',
                      borderRadius: '4px',
                      border: `1px solid ${colors.mediumGray}`
                    }}
                  >
                    <h4 style={{
                      color: colors.secondary,
                      fontSize: '0.95em',
                      marginBottom: '12px'
                    }}>
                      Card {index + 1}
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '15px' }}>
                      <div>
                        <label style={labelStyle}>Título</label>
                        <input
                          type="text"
                          value={card.title}
                          onChange={(e) => handleTextChange('contentCards', 'title', e.target.value, index)}
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Descripción</label>
                        <input
                          type="text"
                          value={card.description}
                          onChange={(e) => handleTextChange('contentCards', 'description', e.target.value, index)}
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Texto del Botón</label>
                        <input
                          type="text"
                          value={card.linkText}
                          onChange={(e) => handleTextChange('contentCards', 'linkText', e.target.value, index)}
                          style={inputStyle}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Botón Guardar Cambios */}
            <div style={{
              marginTop: '25px',
              padding: '20px',
              backgroundColor: colors.lightGray,
              borderRadius: '6px',
              border: `1px solid ${colors.mediumGray}`,
              display: 'flex',
              alignItems: 'center',
              gap: '15px'
            }}>
              <button
                onClick={handleSaveChanges}
                disabled={!hasUnsavedChanges}
                style={{
                  padding: '12px 30px',
                  backgroundColor: colors.green,
                  color: colors.white,
                  border: 'none',
                  borderRadius: '6px',
                  cursor: hasUnsavedChanges ? 'pointer' : 'not-allowed',
                  fontWeight: '600',
                  fontSize: '1em',
                  transition: 'background-color 0.2s ease',
                  opacity: hasUnsavedChanges ? 1 : 0.5
                }}
                onMouseOver={(e) => { if (hasUnsavedChanges) e.target.style.backgroundColor = '#009944'; }}
                onMouseOut={(e) => { if (hasUnsavedChanges) e.target.style.backgroundColor = colors.green; }}
              >
                Guardar Cambios
              </button>
              {hasUnsavedChanges && (
                <span style={{ color: colors.accent, fontSize: '0.9em' }}>
                  * Hay cambios sin guardar
                </span>
              )}
            </div>

          </div>
        )}

      </div>

      <Footer />
    </div>
  );
};

export default CMS;
